import React, { useState, useEffect } from 'react';
import { safeTrim } from '../utils/validation';
import { Plus, Edit, Trash2, Zap, Search, ArrowUp, ArrowDown, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Package, HelpCircle, X, RefreshCw, Download, Upload, Eye, EyeOff, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { getContrastText } from '../utils/colorContrast';
import { useApp } from '../contexts/AppContext';

// Burst Info Collapsible Component (inside form)
const BurstInfoPanel = ({ isOpen, onToggle }) => {
  return (
    <div className="col-span-2">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
      >
        <span className="flex items-center gap-2 text-sm font-medium">
          <HelpCircle className="w-4 h-4" />
          📖 Panduan Konfigurasi Burst QoS
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Ilustrasi */}
          <div className="bg-gray-100 dark:bg-gray-700 p-3 font-mono text-xs">
            <p className="text-gray-500 dark:text-gray-400 mb-1 text-[10px]">Ilustrasi Bandwidth:</p>
            <pre className="text-[10px] leading-relaxed">
{`BURST ────┐       ← Bonus di AWAL
          │ Burst Time
MAX LIMIT ┴────── ← Kecepatan normal
LIMIT-AT ──────── ← Bandwidth TERJAMIN`}
            </pre>
          </div>

          {/* Parameter explanations - compact */}
          <div className="p-3 space-y-2 text-xs">
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-blue-500 flex-shrink-0"></span>
              <div><b>MAX LIMIT:</b> Kecepatan maksimum normal setelah burst habis</div>
            </div>
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-purple-500 flex-shrink-0"></span>
              <div><b>BURST:</b> Kecepatan bonus di AWAL (speed test dapat ini)</div>
            </div>
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-orange-500 flex-shrink-0"></span>
              <div><b>THRESHOLD:</b> Batas untuk aktifkan burst lagi (70-100% MAX LIMIT)</div>
            </div>
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-green-500 flex-shrink-0"></span>
              <div><b>LIMIT-AT:</b> Bandwidth TERJAMIN meski jaringan penuh</div>
            </div>
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-cyan-500 flex-shrink-0"></span>
              <div><b>BURST TIME:</b> Durasi burst (detik) sebelum turun ke MAX LIMIT</div>
            </div>
            <div className="flex gap-2">
              <span className="w-2 h-2 mt-1 rounded-full bg-pink-500 flex-shrink-0"></span>
              <div><b>PRIORITY:</b> 1-8, semakin kecil = semakin prioritas</div>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gray-50 dark:bg-gray-800 p-3 border-t dark:border-gray-700">
            <p className="font-semibold text-xs mb-2">📦 Contoh "Paket 15M Burst 30M":</p>
            <div className="grid grid-cols-3 gap-1 text-[10px]">
              <span>MAX LIMIT: 15M</span>
              <span>BURST: 30M</span>
              <span>THRESHOLD: 15M</span>
              <span>LIMIT-AT: 15M</span>
              <span>Burst Time: 30s</span>
              <span>Priority: 5</span>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 dark:text-slate-400">
              ✅ Speed test: 30M | ✅ Normal: 15M | ✅ Jaringan penuh: 15M terjamin
            </p>
          </div>

          {/* Tips */}
          <div className="bg-yellow-50 dark:bg-yellow-900/30 p-3 border-t dark:border-gray-700">
            <p className="font-semibold text-[10px] text-yellow-800 dark:text-yellow-200">💡 Tips:</p>
            <ul className="text-[10px] text-yellow-700 dark:text-yellow-300 mt-1 space-y-0.5 list-disc list-inside">
              <li>Burst aktif lagi saat pemakaian &lt; THRESHOLD</li>
              <li>THRESHOLD = MAX LIMIT → burst lebih sering aktif</li>
              <li>LIMIT-AT = MAX LIMIT → bandwidth selalu terjamin penuh</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicePlans = () => {
  const { token, user } = useAuth();
  const { branding } = useBranding();
  const { selectedBranch } = useApp(); // Get branch filter from global context
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  // Format duration label
  const formatDurationLabel = (plan) => {
    if ((plan.package_type || "pppoe") === "pppoe") return "bulan";
    const p = Number(plan.validity_period || 0);
    if (p <= 0) return "unlimited";
    const unitMap = { hours: "jam", days: "hari", weeks: "minggu", months: "bulan" };
    const unit = unitMap[plan.validity_unit] || plan.validity_unit || "hari";
    return `${p} ${unit}`;
  };

  const [branches, setBranches] = useState([]);
  const [connectionTypeFilter, setConnectionTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isBurstInfoOpen, setIsBurstInfoOpen] = useState(false);
  
  // Sync states
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [mikrotikProfiles, setMikrotikProfiles] = useState({ ppp: [], hotspot: [], pools: [] });
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [importPrices, setImportPrices] = useState({});
  const [mikrotikRouters, setMikrotikRouters] = useState([]);
  const [selectedRouter, setSelectedRouter] = useState('all'); // 'all' or specific router id
  const [selectedRouterInfo, setSelectedRouterInfo] = useState(null);
  
  // IP Pools from database
  const [ipPools, setIpPools] = useState([]);
  
  // IP Pool configuration for sync
  const [poolConfig, setPoolConfig] = useState({
    name: 'RadiusLink',
    rangeStart: '172.16.0.2',
    rangeEnd: '172.16.31.254',
    localAddress: '172.16.0.1',
    // Expired pool config
    expiredEnabled: true,
    expiredName: 'RadiusLink-Expired',
    expiredRangeStart: '10.10.99.2',
    expiredRangeEnd: '10.10.99.254',
    expiredLocalAddress: '10.10.99.1',
    expiredBandwidth: '128k', // 128kbps
    expiredRedirectUrl: '', // URL halaman isolir (akan di-set dari settings)
    setupNatIsolir: true // Auto setup NAT untuk redirect isolir
  });
  
  // Check if user is owner (read-only mode)
  const isOwnerRole = user?.role?.toLowerCase() === 'owner';
  // Branch-scoped user: hanya bisa create/edit paket untuk cabang mereka sendiri
  const userBranchId = user?.branch_id ? String(user.branch_id) : null;
  const isBranchScoped = ['kepala_cabang', 'teknisi', 'customer service'].includes(user?.role?.toLowerCase()) && userBranchId;
  const defaultBranchId = isBranchScoped ? userBranchId : 'all';
  
  const [formData, setFormData] = useState({
    name: '',
    package_type: 'pppoe',
    branch_id: defaultBranchId, // 'all' for admin; user's branch for branch-scoped roles
    validity_period: '',
    validity_unit: 'days',
    speed_type: 'FIX',
    download_speed: 0,
    upload_speed: 0,
    burst_download_speed: 0,
    burst_upload_speed: 0,
    burst_threshold_download: 0,
    burst_threshold_upload: 0,
    burst_time_download: 20,
    burst_time_upload: 20,
    burst_priority: 8,
    limit_at_download: 0,
    limit_at_upload: 0,
    price: 0,
    shared_users: 1,
    login_days: 'Mo,Tu,We,Th,Fr,Sa,Su',
    login_time_from: '00:00',
    login_time_to: '23:59',
    quota_gb: '',
    description: '',
    is_active: true,
    is_public: true,
    is_featured: false,
    auto_create_mikrotik_profile: false,
    mikrotik_profile_name: '',
    // IP Pool selection (from dropdown)
    selected_ip_pool_id: '',
    // Router selection for MikroTik profile creation
    selected_router_id: ''
  });
  const [displayPrice, setDisplayPrice] = useState('');

  const formatNumber = (value) => {
    if (!value && value !== 0) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const parseFormattedNumber = (value) => {
    if (!value) return 0;
    return parseFloat(value.replace(/\./g, '')) || 0;
  };

  useEffect(() => {
    const loadPlans = async () => {
      if (!token) return;
      try {
        // Use API v2 (MySQL)
        const response = await axios.get(`${API_URL}/api/v2/service-plans`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setPlans(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };
    
    const loadBranches = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/branches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setBranches(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    };
    
    const loadMikrotikRouters = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/mikrotiks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setMikrotikRouters(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching MikroTik routers:', error);
      }
    };
    
    const loadIpPools = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/v2/ip-pools`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setIpPools(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching IP pools:', error);
      }
    };
    
    loadPlans();
    loadBranches();
    loadMikrotikRouters();
    loadIpPools();
  }, [token]);

  const fetchPlans = async () => {
    if (!token) return;
    try {
      // Use API v2 (MySQL)
      const response = await axios.get(`${API_URL}/api/v2/service-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // Get branch name by ID - use stored branch_name first, then lookup
  const getBranchName = (plan) => {
    // First check if branch_name is stored in plan
    if (plan.branch_name) return plan.branch_name;
    
    // Fallback to lookup
    const branchId = plan.branch_id;
    if (!branchId || branchId === 'all') return 'Semua Cabang';
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || branchId;
  };

  useEffect(() => {
    let filtered = plans;
    
    // Filter by branch using global selectedBranch from context
    if (selectedBranch && selectedBranch !== 'all') {
      // Hanya tampilkan paket dengan branch_id = filter ATAU branch_id = NULL/empty (semua cabang)
      // Convert to string for comparison since selectedBranch is string from context
      const filterValue = String(selectedBranch);
      filtered = filtered.filter(p => {
        const planBranchId = p.branch_id;
        // Show if: branch matches OR plan is for all branches (null/empty/'all')
        return String(planBranchId) === filterValue || 
               planBranchId === null || 
               planBranchId === '' || 
               planBranchId === 'all' ||
               planBranchId === undefined;
      });
    }
    
    // Filter by connection type
    if (connectionTypeFilter === 'pppoe') {
      filtered = filtered.filter(p => p.package_type === 'pppoe' || p.package_type === 'all' || !p.package_type);
    } else if (connectionTypeFilter === 'hotspot') {
      filtered = filtered.filter(p => p.package_type === 'hotspot' || p.package_type === 'all');
    }
    // 'all' shows all plans, so no filtering needed
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredPlans(filtered);
  }, [plans, selectedBranch, connectionTypeFilter, searchTerm, sortConfig]);

  useEffect(() => {
    setDisplayPrice(formatNumber(formData.price));
  }, [formData.price]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // prevent double-submit
    
    if (!safeTrim(formData.name)) {
      toast.error('Nama paket harus diisi');
      return;
    }
    if (!formData.download_speed || formData.download_speed <= 0) {
      toast.error('Kecepatan download harus lebih dari 0');
      return;
    }
    if (!formData.upload_speed || formData.upload_speed <= 0) {
      toast.error('Kecepatan upload harus lebih dari 0');
      return;
    }
    // Harga 0 = Free (paket gratis untuk member dll) — diizinkan
    if (formData.price < 0) {
      toast.error('Harga tidak boleh negatif');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        quota_gb: formData.quota_gb ? parseInt(formData.quota_gb) : null,
        validity_period: (formData.validity_period === "" || formData.validity_period == null) ? null : (parseInt(formData.validity_period) || 0)
      };

      let response;
      if (isEditMode) {
        // Use API v2 (MySQL)
        response = await axios.put(`${API_URL}/api/v2/service-plans/${editingPlanId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Paket berhasil diupdate');
      } else {
        // Use API v2 (MySQL)
        response = await axios.post(`${API_URL}/api/v2/service-plans`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Paket berhasil ditambahkan');
      }

      // Show MikroTik result if auto-create was enabled
      if (response.data.mikrotik && submitData.auto_create_mikrotik_profile) {
        const mtResult = response.data.mikrotik;
        if (mtResult.created_on && mtResult.created_on.length > 0) {
          toast.success(`✅ MikroTik Profile & IP Pool berhasil dibuat di: ${mtResult.created_on.join(', ')}`, { duration: 6000 });
        }
        if (mtResult.failed_on && mtResult.failed_on.length > 0) {
          toast.warning(`⚠️ Gagal di: ${mtResult.failed_on.join(', ')}`, { duration: 6000 });
        }
        if (mtResult.skipped_offline && mtResult.skipped_offline.length > 0) {
          toast.info(`ℹ️ Skip router offline: ${mtResult.skipped_offline.join(', ')}`, { duration: 5000 });
        }
        if (mtResult.error) {
          toast.error(`❌ Error MikroTik: ${mtResult.error}`, { duration: 6000 });
        }
      }
      
      setIsDialogOpen(false);
      setIsEditMode(false);
      setEditingPlanId(null);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error('Service plan error:', error);
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        } else {
          errorMsg = error.response.data.detail;
        }
      }
      // Parse error message untuk pesan yang lebih jelas
      let friendlyMsg = errorMsg;
      if (errorMsg.includes('Duplicate entry') && errorMsg.includes('name')) {
        friendlyMsg = 'Nama paket sudah digunakan. Silakan gunakan nama lain.';
      } else if (errorMsg.includes('Duplicate entry')) {
        friendlyMsg = 'Data sudah ada. Silakan periksa kembali input Anda.';
      } else if (errorMsg.includes('tenant_id')) {
        friendlyMsg = 'Terjadi kesalahan sistem. Silakan refresh halaman dan coba lagi.';
      }
      toast.error(isEditMode ? `❌ Gagal update: ${friendlyMsg}` : `❌ Gagal menambahkan: ${friendlyMsg}`, { duration: 5000 });
    } finally { setIsSubmitting(false); }
  };

  const handleEdit = (plan) => {
    setIsEditMode(true);
    setEditingPlanId(plan.id);
    setFormData({
      name: plan.name,
      package_type: plan.package_type || 'pppoe',
      branch_id: isBranchScoped ? userBranchId : (plan.branch_id || 'all'),
      validity_period: (plan.validity_period == null ? '' : Number(plan.validity_period)),
      validity_unit: plan.validity_unit || 'days',
      speed_type: plan.speed_type || ((Number(plan.burst_download_speed) > 0 || Number(plan.burst_upload_speed) > 0) ? 'Burst' : 'FIX'),
      download_speed: plan.download_speed,
      upload_speed: plan.upload_speed,
      shared_users: plan.shared_users || 1,
      login_days: plan.login_days || 'Mo,Tu,We,Th,Fr,Sa,Su',
      login_time_from: plan.login_time_from || '00:00',
      login_time_to: plan.login_time_to || '23:59',
      burst_download_speed: plan.burst_download_speed || 0,
      burst_upload_speed: plan.burst_upload_speed || 0,
      burst_threshold_download: plan.burst_threshold_download || 0,
      burst_threshold_upload: plan.burst_threshold_upload || 0,
      burst_time_download: plan.burst_time_download || 20,
      burst_time_upload: plan.burst_time_upload || 20,
      burst_priority: plan.burst_priority || 8,
      limit_at_download: plan.limit_at_download || 0,
      limit_at_upload: plan.limit_at_upload || 0,
      price: plan.price,
      quota_gb: plan.quota_gb || '',
      description: plan.description || '',
      is_active: plan.is_active,
      is_public: plan.is_public === undefined ? true : !!plan.is_public,
      is_featured: !!plan.is_featured,
      auto_create_mikrotik_profile: plan.auto_create_mikrotik_profile || false,
      mikrotik_profile_name: plan.mikrotik_profile_name || '',
      selected_ip_pool_id: plan.selected_ip_pool_id || '',
      selected_router_id: plan.selected_router_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus paket ini?')) return;
    
    try {
      // Use API v2 (MySQL)
      await axios.delete(`${API_URL}/api/v2/service-plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paket berhasil dihapus');
      await fetchPlans();
    } catch (error) {
      toast.error('Gagal menghapus paket: ' + (error.response?.data?.detail || error.message));
    }
  };

  const toggleStatus = async (plan) => {
    try {
      await axios.put(`${API_URL}/api/v2/service-plans/${plan.id}`, 
        { is_active: !plan.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Paket ${!plan.is_active ? 'diaktifkan' : 'dinonaktifkan'}`);
      fetchPlans();
    } catch (error) {
      toast.error('Gagal mengubah status');
    }
  };

  const togglePublic = async (plan) => {
    const current = plan.is_public === undefined ? true : !!plan.is_public;
    const next = !current;
    try {
      await axios.put(`${API_URL}/api/v2/service-plans/${plan.id}`,
        { is_public: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(next ? 'Paket sekarang TAMPIL di form daftar publik' : 'Paket disembunyikan dari form daftar publik');
      fetchPlans();
    } catch (error) {
      toast.error('Gagal mengubah status publish');
    }
  };

  const toggleFeatured = async (plan) => {
    const next = !plan.is_featured;
    try {
      await axios.put(`${API_URL}/api/v2/service-plans/${plan.id}`,
        { is_featured: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(next ? 'Paket ditandai sebagai "Rekomendasi"' : 'Tanda rekomendasi dihapus');
      fetchPlans();
    } catch (error) {
      toast.error('Gagal mengubah featured');
    }
  };

  // Sync all plans to MikroTik (Push)
  const handleSyncToMikrotik = async () => {
    setIsSyncing(true);
    try {
      const params = {
        router_id: selectedRouter !== 'all' ? selectedRouter : null,
        ip_pool: {
          name: poolConfig.name,
          range_start: poolConfig.rangeStart,
          range_end: poolConfig.rangeEnd,
          local_address: poolConfig.localAddress
        }
      };
      
      // Add expired pool config if enabled
      if (poolConfig.expiredEnabled) {
        params.expired_pool = {
          name: poolConfig.expiredName,
          range_start: poolConfig.expiredRangeStart,
          range_end: poolConfig.expiredRangeEnd,
          local_address: poolConfig.expiredLocalAddress,
          bandwidth: poolConfig.expiredBandwidth,
          redirect_url: poolConfig.expiredRedirectUrl
        };
      }
      
      console.log('[SYNC] Sending request with params:', params);
      
      const response = await axios.post(`${API_URL}/api/mikrotik/sync-all-profiles`, params, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[SYNC] Response:', response.data);
      
      // Show detailed result
      const data = response.data;
      if (data.success) {
        const routerInfo = data.router_name || 'MikroTik';
        let msg = `✅ Sync ke ${routerInfo}: ${data.synced}/${data.total} profile`;
        if (data.pool_created) {
          msg += ` | Pool dibuat`;
        }
        if (data.expired_pool_created) {
          msg += ` | Expired pool dibuat`;
        }
        
        // Show router results if available
        if (data.router_results && data.router_results.length > 0) {
          data.router_results.forEach(r => {
            console.log(`[SYNC] Router ${r.router}: synced=${r.synced}, failed=${r.failed}, success=${r.success}, msg=${r.message}`);
            
            // Show detail errors if any failed
            if (r.details && r.details.length > 0) {
              r.details.forEach(d => {
                console.log(`[SYNC] Profile ${d.name}: success=${d.success}, pppoe=${d.pppoe}, hotspot=${d.hotspot}`);
              });
              
              // Show first error in toast if there are failures
              const failedProfiles = r.details.filter(d => !d.success);
              if (failedProfiles.length > 0 && r.synced === 0) {
                const firstError = failedProfiles[0];
                const errorMsg = firstError.pppoe || firstError.hotspot || 'Unknown error';
                toast.error(`❌ Gagal: ${errorMsg}`, { duration: 10000 });
              }
            }
          });
        }
        
        if (data.synced > 0) {
          toast.success(msg, { duration: 5000 });
        } else {
          toast.warning(`⚠️ ${msg} - Tidak ada profile berhasil di-sync`, { duration: 5000 });
        }
        
        // Auto setup NAT isolir if enabled and sync was successful
        if (poolConfig.setupNatIsolir && poolConfig.expiredEnabled && data.router_results) {
          for (const routerResult of data.router_results) {
            if (routerResult.success) {
              // Find router ID from mikrotikRouters by matching host
              const router = mikrotikRouters.find(r => r.host === routerResult.host);
              if (router) {
                try {
                  console.log(`[NAT] Setting up isolir NAT for router ${router.id}`);
                  const natResponse = await axios.post(`${API_URL}/api/mikrotiks/${router.id}/auto-setup-isolir`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  if (natResponse.data.success) {
                    toast.success(`✅ NAT Isolir berhasil di-setup pada ${routerResult.router}`, { duration: 3000 });
                  }
                } catch (natError) {
                  console.error(`[NAT] Failed to setup isolir NAT:`, natError);
                  toast.error(`⚠️ Setup NAT Isolir gagal pada ${routerResult.router}`, { duration: 5000 });
                }
              }
            }
          }
        }
        
        setIsSyncDialogOpen(false);
        setSelectedRouter('all');
        fetchPlans();
      } else {
        // Show detailed error
        let errorMsg = data.message || 'Sync gagal';
        if (data.router_results) {
          const failedRouters = data.router_results.filter(r => !r.success);
          if (failedRouters.length > 0) {
            errorMsg += ': ' + failedRouters.map(r => `${r.router}: ${r.message}`).join(', ');
          }
        }
        toast.error(errorMsg, { duration: 8000 });
      }
    } catch (error) {
      console.error('[SYNC] Error:', error);
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || error.message;
      toast.error('Gagal sync: ' + errorDetail, { duration: 8000 });
    } finally {
      setIsSyncing(false);
    }
  };

  // Load profiles from MikroTik (for Import)
  const loadMikrotikProfiles = async (routerId = null) => {
    setIsLoadingProfiles(true);
    try {
      const params = routerId && routerId !== 'all' ? `?router_id=${routerId}` : '';
      const response = await axios.get(`${API_URL}/api/mikrotik/profiles${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMikrotikProfiles({
          ppp: response.data.ppp_profiles || [],
          hotspot: response.data.hotspot_profiles || [],
          pools: response.data.address_pools || []
        });
        setSelectedRouterInfo(response.data.router || null);
        setSelectedProfiles([]);
        setImportPrices({});
      } else {
        toast.error('Gagal load profiles dari MikroTik');
      }
    } catch (error) {
      toast.error('Gagal load profiles: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  // Import selected profiles from MikroTik
  const handleImportProfiles = async () => {
    if (selectedProfiles.length === 0) {
      toast.error('Pilih minimal 1 profile untuk diimport');
      return;
    }
    
    setIsSyncing(true);
    try {
      const profilesToImport = selectedProfiles.map(p => ({
        name: p.name,
        download_speed: p.download_speed || 10,
        upload_speed: p.upload_speed || 10,
        burst_download_speed: p.burst_download_speed || 0,
        burst_upload_speed: p.burst_upload_speed || 0,
        burst_threshold_download: p.burst_threshold_download || 0,
        burst_threshold_upload: p.burst_threshold_upload || 0,
        burst_time_download: p.burst_time_download || 20,
        burst_time_upload: p.burst_time_upload || 20,
        price: importPrices[p.name] || 0,
        package_type: p.type || 'pppoe'
      }));
      
      const response = await axios.post(`${API_URL}/api/mikrotik/import-profiles`, {
        profiles: profilesToImport
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`Import berhasil: ${response.data.imported} profile diimport`);
        setIsImportDialogOpen(false);
        fetchPlans();
      } else {
        toast.error(response.data.message || 'Import gagal');
      }
    } catch (error) {
      toast.error('Gagal import profiles: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSyncing(false);
    }
  };

  // Toggle profile selection for import
  const toggleProfileSelection = (profile, type) => {
    const profileWithType = { ...profile, type };
    const isSelected = selectedProfiles.some(p => p.name === profile.name && p.type === type);
    
    if (isSelected) {
      setSelectedProfiles(prev => prev.filter(p => !(p.name === profile.name && p.type === type)));
    } else {
      setSelectedProfiles(prev => [...prev, profileWithType]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      package_type: connectionTypeFilter,
      branch_id: defaultBranchId,
      validity_period: '',
      validity_unit: 'days',
      speed_type: 'FIX',
      download_speed: 0,
      upload_speed: 0,
      burst_download_speed: 0,
      burst_upload_speed: 0,
      burst_threshold_download: 0,
      burst_threshold_upload: 0,
      burst_time_download: 20,
      burst_time_upload: 20,
      burst_priority: 8,
      limit_at_download: 0,
      limit_at_upload: 0,
      price: 0,
      shared_users: 1,
      login_days: 'Mo,Tu,We,Th,Fr,Sa,Su',
      login_time_from: '00:00',
      login_time_to: '23:59',
      quota_gb: '',
      description: '',
      is_active: true,
      is_public: true,
      is_featured: false,
      auto_create_mikrotik_profile: false,
      mikrotik_profile_name: '',
      selected_ip_pool_id: '',
    // Router selection for MikroTik profile creation
    selected_router_id: ''
    });
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="min-h-screen p-4 md:p-6 bg-slate-50 pb-20 dark:bg-slate-950">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="relative p-3 rounded-xl shadow-md" style={{ background: `linear-gradient(135deg, ${branding?.primary_color || '#4F46E5'} 0%, ${branding?.secondary_color || '#7c3aed'} 100%)`, boxShadow: '0 4px 14px rgba(79, 70, 229, 0.3)' }}>
            <Package className="w-6 h-6 drop-shadow" strokeWidth={2.4} style={{ color: getContrastText(branding?.primary_color || '#4F46E5') }} />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200">Paket Layanan</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {isOwnerRole ? 'Daftar paket internet (hanya lihat)' : 'Kelola paket internet pelanggan'}
            </p>
          </div>
        </div>
        
        {/* Action buttons for admin */}
        {!isOwnerRole && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Sync Buttons */}
          <Button 
            variant="outline"
            size="sm"
            className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            onClick={() => setIsSyncDialogOpen(true)}
          >
            <Upload className="w-4 h-4 mr-1" /> Push ke MikroTik
          </Button>
          <Button 
            variant="outline"
            size="sm"
            className="text-blue-600 border-blue-300 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
            onClick={() => { setIsImportDialogOpen(true); loadMikrotikProfiles(); }}
          >
            <Download className="w-4 h-4 mr-1" /> Import dari MikroTik
          </Button>
          
          {/* Add Plan Button */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="transition-colors"
                style={{
                  borderColor: branding.primary_color || '#4F46E5',
                  color: branding.primary_color || '#4F46E5',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${branding.primary_color || '#4F46E5'}15`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => { setIsEditMode(false); setEditingPlanId(null); resetForm(); }}
              >
                <Plus className="w-4 h-4 mr-2" /> Tambah Paket
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader className="border-b pb-3">
                <DialogTitle className="text-lg font-semibold">{isEditMode ? 'Edit Paket' : 'Tambah Paket Baru'}</DialogTitle>
                <p className="text-xs text-slate-500 dark:text-slate-400">Lengkapi informasi paket layanan internet</p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                {/* Section: Informasi Dasar */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold dark:bg-indigo-900/40 dark:text-indigo-400">1</div>
                    Informasi Dasar
                  </div>
                  <div className="grid grid-cols-2 gap-3 pl-7">
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs font-medium">Nama Paket <span className="text-red-500">*</span></Label>
                      <Input 
                        required 
                      placeholder="Contoh: Paket Home 10Mbps" 
                      value={formData.name}
                      className="h-9 text-sm"
                      onChange={(e) => {
                        const nm = e.target.value;
                        const nu = nm.toUpperCase();
                        // Auto-detect validity_unit from name pattern (only suggest if user hasn't manually picked non-default)
                        let detectedUnit = null;
                        if (/JAM|HOUR/.test(nu)) detectedUnit = 'hours';
                        else if (/MINGGU|WEEK/.test(nu)) detectedUnit = 'days'; // will multiply below
                        else if (/BULAN|MONTH/.test(nu)) detectedUnit = 'months';
                        const patch = { ...formData, name: nm };
                        if (detectedUnit && formData.validity_unit !== detectedUnit) {
                          patch.validity_unit = detectedUnit;
                        }
                        setFormData(patch);
                      }} 
                    />
                  </div>

                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs font-medium">Cabang</Label>
                    <select 
                      className={"w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent " + (isBranchScoped ? "bg-slate-100 cursor-not-allowed" : "bg-white")}
                      value={formData.branch_id || 'all'}
                      disabled={isBranchScoped}
                      title={isBranchScoped ? 'Anda hanya dapat membuat paket untuk cabang Anda sendiri' : undefined}
                      onChange={(e) => {
                        if (isBranchScoped) return;
                        const newBranchId = e.target.value;
                        // Reset selected_router_id if it no longer matches the new branch
                        const curRouter = mikrotikRouters.find(r => String(r.id) === String(formData.selected_router_id));
                        const stillValid = !curRouter || newBranchId === 'all' || String(curRouter.branch_id || '') === String(newBranchId);
                        setFormData(prev => ({
                          ...prev,
                          branch_id: newBranchId,
                          selected_router_id: stillValid ? prev.selected_router_id : ''
                        }));
                      }}
                      data-testid="package-form-branch-select"
                    >
                      {!isBranchScoped && (
                        <option value="all">Semua Cabang (Berlaku untuk semua)</option>
                      )}
                      {branches.filter(b => b.is_active).filter(b => !isBranchScoped || String(b.id) === userBranchId).map(branch => (
                        <option key={branch.id} value={branch.id}>{branch.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs font-medium">Tipe Paket <span className="text-red-500">*</span></Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, package_type: 'pppoe'})}
                        className={`flex items-center justify-center gap-2 h-10 px-3 text-sm font-medium rounded-md border-2 transition-all ${
                          formData.package_type === 'pppoe'
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="package-type-pppoe-btn"
                      >
                        <span className="text-base">🏠</span>
                        <span>PPPoE</span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">(Bulanan)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, package_type: 'hotspot'})}
                        className={`flex items-center justify-center gap-2 h-10 px-3 text-sm font-medium rounded-md border-2 transition-all ${
                          formData.package_type === 'hotspot'
                            ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                        data-testid="package-type-hotspot-btn"
                      >
                        <span className="text-base">📶</span>
                        <span>Hotspot</span>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">(Voucher)</span>
                      </button>
                    </div>
                  </div>

                  {formData.package_type === 'hotspot' && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Durasi <span className="text-red-500">*</span></Label>
                        <Input 
                          type="number" 
                          required={formData.package_type === 'hotspot'} 
                          placeholder="0 = Unlimited" 
                          min="0"
                          className="h-9 text-sm"
                          value={formData.validity_period === '' || formData.validity_period == null ? '' : formData.validity_period}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFormData({...formData, validity_period: v === '' ? 0 : (parseInt(v) || 0)});
                          }}
                          data-testid="input-validity-period"
                        />
                        <p className="text-[10px] text-slate-500 mt-0.5 dark:text-slate-400">Isi 0 = berlaku Unlimited (tanpa batas waktu)</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Unit <span className="text-red-500">*</span></Label>
                        <select 
                          className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-700 dark:bg-slate-800"
                          value={formData.validity_unit}
                          onChange={(e) => setFormData({...formData, validity_unit: e.target.value})}
                        >
                          <option value="hours">Jam</option>
                          <option value="days">Hari</option>
                          <option value="months">Bulan</option>
                        </select>
                        {formData.validity_period ? (
                          <p className={"text-[10px] mt-0.5 font-medium " + (
                            /JAM|HOUR/i.test(formData.name||'') && formData.validity_unit !== 'hours' ? 'text-red-500' :
                            /BULAN|MONTH/i.test(formData.name||'') && formData.validity_unit !== 'months' ? 'text-red-500' :
                            'text-emerald-600 dark:text-emerald-400'
                          )}>
                            {(/JAM|HOUR/i.test(formData.name||'') && formData.validity_unit !== 'hours') ||
                             (/BULAN|MONTH/i.test(formData.name||'') && formData.validity_unit !== 'months') ? 'PERINGATAN: nama paket tidak sesuai unit!' :
                              'Total durasi: ' + formData.validity_period + ' ' + ({hours:'Jam',days:'Hari',months:'Bulan'}[formData.validity_unit]||formData.validity_unit)}
                          </p>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Section: Konfigurasi Kecepatan */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold dark:bg-indigo-900/40 dark:text-indigo-400">2</div>
                  Konfigurasi Kecepatan
                </div>
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs font-medium">Jenis Kecepatan <span className="text-red-500">*</span></Label>
                    <select 
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:border-slate-700 dark:bg-slate-800"
                      value={formData.speed_type}
                      onChange={(e) => setFormData({...formData, speed_type: e.target.value})}
                    >
                      <option value="FIX">FIX (Kecepatan Tetap)</option>
                      <option value="Burst">Burst (Kecepatan Dinamis)</option>
                    </select>
                  </div>

                  {formData.speed_type === 'FIX' && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Download (Mbps) <span className="text-red-500">*</span></Label>
                        <Input 
                          type="number" 
                          required 
                          placeholder="10" 
                          value={formData.download_speed}
                          className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, download_speed: parseInt(e.target.value) || 0})} 
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Upload (Mbps) <span className="text-red-500">*</span></Label>
                        <Input 
                          type="number" 
                          required 
                          placeholder="5" 
                          value={formData.upload_speed}
                          className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, upload_speed: parseInt(e.target.value) || 0})} 
                        />
                      </div>
                    </>
                  )}

                  {formData.speed_type === 'Burst' && (
                    <>
                      <div className="col-span-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="font-semibold text-blue-900 text-xs flex items-center gap-2">
                          <Zap className="w-3 h-3" /> Burst Speed Configuration
                        </h4>
                        <p className="text-xs text-blue-700 dark:text-blue-300">Kecepatan lebih tinggi saat traffic rendah</p>
                      </div>
                      
                      <BurstInfoPanel isOpen={isBurstInfoOpen} onToggle={() => setIsBurstInfoOpen(!isBurstInfoOpen)} />
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">MAX LIMIT Download (Mbps) <span className="text-red-500">*</span></Label>
                        <Input type="number" required placeholder="10" value={formData.download_speed} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, download_speed: parseInt(e.target.value) || 0})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">MAX LIMIT Upload (Mbps) <span className="text-red-500">*</span></Label>
                        <Input type="number" required placeholder="5" value={formData.upload_speed} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, upload_speed: parseInt(e.target.value) || 0})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">BURST Download (Mbps) <span className="text-red-500">*</span></Label>
                        <Input type="number" required placeholder="20" value={formData.burst_download_speed} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_download_speed: parseInt(e.target.value) || 0})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">BURST Upload (Mbps) <span className="text-red-500">*</span></Label>
                        <Input type="number" required placeholder="10" value={formData.burst_upload_speed} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_upload_speed: parseInt(e.target.value) || 0})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">THRESHOLD Download (Mbps)</Label>
                        <Input type="number" placeholder="8" value={formData.burst_threshold_download} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_threshold_download: parseInt(e.target.value) || 0})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">THRESHOLD Upload (Mbps)</Label>
                        <Input type="number" placeholder="4" value={formData.burst_threshold_upload} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_threshold_upload: parseInt(e.target.value) || 0})} />
                      </div>
                      
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Burst Time Download (detik)</Label>
                        <Input type="number" placeholder="60" value={formData.burst_time_download} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_time_download: parseInt(e.target.value) || 60})} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium">Burst Time Upload (detik)</Label>
                        <Input type="number" placeholder="60" value={formData.burst_time_upload} className="h-9 text-sm"
                          onChange={(e) => setFormData({...formData, burst_time_upload: parseInt(e.target.value) || 60})} />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Section: Harga & Detail */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold dark:bg-indigo-900/40 dark:text-indigo-400">3</div>
                  Harga & Detail
                </div>
                <div className="grid grid-cols-2 gap-3 pl-7">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Harga (Rp) <span className="text-red-500">*</span></Label>
                    <Input 
                      type="text" 
                      required 
                      placeholder="150.000" 
                      value={displayPrice}
                      className="h-9 text-sm"
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/\./g, '');
                        if (/^\d*$/.test(rawValue)) {
                          setFormData({...formData, price: parseFloat(rawValue) || 0});
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFormattedNumber(e.target.value);
                        setDisplayPrice(formatNumber(value));
                      }} 
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Kuota (GB)</Label>
                    <Input 
                      type="number" 
                      placeholder="Unlimited" 
                      value={formData.quota_gb}
                      className="h-9 text-sm"
                      onChange={(e) => setFormData({...formData, quota_gb: e.target.value})} 
                    />
                  </div>
                  {formData.package_type === 'hotspot' && (
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Shared Users</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        placeholder="1"
                        className="h-9 text-sm"
                        value={formData.shared_users ?? 1}
                        onChange={(e) => {
                          const v = parseInt(e.target.value) || 1;
                          setFormData({ ...formData, shared_users: Math.max(1, Math.min(100, v)) });
                        }}
                        data-testid="input-shared-users"
                      />
                      <p className="text-[10px] text-slate-500 mt-0.5 dark:text-slate-400">Jumlah perangkat yang boleh login bersamaan dengan 1 voucher (contoh: 10)</p>
                    </div>
                  )}

                  {formData.package_type === 'hotspot' && (
                    <div className="col-span-2 mt-1 p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3" data-testid="login-period-section">
                      <div>
                        <Label className="text-xs font-semibold text-slate-700 dark:text-slate-200">! Periode Login</Label>
                        <p className="text-[10px] text-slate-500 mt-0.5 dark:text-slate-400">Atur hari & jam pelanggan boleh login. Default: setiap hari, 24 jam.</p>
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.login_days || '').split(',').filter(Boolean).length === 7}
                          onChange={(e) => setFormData({
                            ...formData,
                            login_days: e.target.checked ? 'Mo,Tu,We,Th,Fr,Sa,Su' : ''
                          })}
                          data-testid="login-allday"
                          className="rounded"
                        />
                        <span className="text-xs italic text-slate-600 dark:text-slate-300">Setiap Hari (Pilih Semua)</span>
                      </label>

                      <div className="flex flex-wrap gap-2">
                        {[
                          {k: 'Mo', l: 'SENIN'},
                          {k: 'Tu', l: 'SELASA'},
                          {k: 'We', l: 'RABU'},
                          {k: 'Th', l: 'KAMIS'},
                          {k: 'Fr', l: "JUM'AT"},
                          {k: 'Sa', l: 'SABTU'},
                          {k: 'Su', l: 'MINGGU'},
                        ].map(({k, l}) => {
                          const sel = (formData.login_days || '').split(',').includes(k);
                          return (
                            <button
                              key={k}
                              type="button"
                              data-testid={`login-day-${k}`}
                              onClick={() => {
                                const cur = (formData.login_days || '').split(',').filter(Boolean);
                                const next = sel ? cur.filter(x => x !== k) : [...cur, k];
                                const order = ['Mo','Tu','We','Th','Fr','Sa','Su'];
                                setFormData({...formData, login_days: order.filter(d => next.includes(d)).join(',')});
                              }}
                              className={`text-[10px] px-2 py-1 rounded border transition-all ${sel
                                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                                : 'bg-white dark:bg-slate-900 text-slate-600 border-slate-300 hover:border-indigo-400'}`}
                            >
                              {l}
                            </button>
                          );
                        })}
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <div className="space-y-1">
                          <Label className="text-[11px] font-medium">! Dari Jam</Label>
                          <Input
                            type="time"
                            value={formData.login_time_from || '00:00'}
                            onChange={(e) => setFormData({...formData, login_time_from: e.target.value})}
                            data-testid="login-time-from"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] font-medium">! Sampai Jam</Label>
                          <Input
                            type="time"
                            value={formData.login_time_to || '23:59'}
                            onChange={(e) => setFormData({...formData, login_time_to: e.target.value})}
                            data-testid="login-time-to"
                            className="h-9 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-span-2 space-y-1">
                    <Label className="text-xs font-medium">Deskripsi</Label>
                    <textarea 
                      className="w-full p-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none dark:border-slate-700" 
                      rows="2"
                      placeholder="Deskripsi paket layanan..." 
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})} 
                    />
                  </div>
                </div>
              </div>

              {/* Section: Pengaturan */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold dark:bg-indigo-900/40 dark:text-indigo-400">4</div>
                  Pengaturan
                </div>
                <div className="pl-7 space-y-2">
                  <label className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors dark:border-slate-700 dark:hover:bg-slate-700/50">
                    <input 
                      type="checkbox" 
                      id="auto-mt" 
                      checked={formData.auto_create_mikrotik_profile}
                      onChange={(e) => setFormData({...formData, auto_create_mikrotik_profile: e.target.checked})} 
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 dark:text-indigo-400" 
                    />
                    <div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Otomatis buat Profile di MikroTik</p>
                    </div>
                  </label>
                  
                  {/* IP Pool Selection - Dropdown from database */}
                  {formData.auto_create_mikrotik_profile && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2 dark:bg-blue-900/30">
                      <p className="text-xs font-semibold text-blue-800">🌐 Pilih IP Pool</p>
                      <select
                        value={formData.selected_ip_pool_id || ''}
                        onChange={(e) => setFormData({...formData, selected_ip_pool_id: e.target.value})}
                        className="w-full px-2 py-1.5 border border-blue-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800"
                      >
                        <option value="">-- Pilih IP Pool --</option>
                        {ipPools.filter(p => p.is_active).map(pool => (
                          <option key={pool.id} value={pool.id}>
                            {pool.name} ({pool.range_start} - {pool.range_end})
                          </option>
                        ))}
                      </select>
                      {ipPools.length === 0 && (
                        <p className="text-xs text-orange-600 dark:text-orange-400">
                          ⚠️ Belum ada IP Pool. <a href="/app/ip-pools" className="underline font-medium">Buat IP Pool dulu</a>
                        </p>
                      )}
                      {formData.selected_ip_pool_id && (
                        <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded dark:text-blue-300 dark:bg-blue-900/40">
                          {(() => {
                            const selectedPool = ipPools.find(p => p.id == formData.selected_ip_pool_id);
                            if (selectedPool) {
                              return (
                                <>
                                  <strong>{selectedPool.name}</strong><br/>
                                  Range: {selectedPool.range_start} - {selectedPool.range_end}<br/>
                                  Gateway: {selectedPool.local_address || '10.10.10.1'}
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  

                  {/* Router Selection - Dropdown for MikroTik profile creation */}
                  {formData.auto_create_mikrotik_profile && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2 dark:bg-emerald-900/30">
                      <p className="text-xs font-semibold text-emerald-800">📡 Pilih Router MikroTik</p>
                      {(() => {
                        const branchSel = String(formData.branch_id || 'all');
                        const filteredRouters = mikrotikRouters
                          .filter(r => r.is_active)
                          .filter(r => branchSel === 'all' ? true : String(r.branch_id || '') === branchSel);
                        return (
                          <>
                            <select
                              data-testid="router-select-dropdown"
                              value={formData.selected_router_id || ''}
                              onChange={(e) => setFormData({...formData, selected_router_id: e.target.value})}
                              className="w-full px-2 py-1.5 border border-emerald-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-800"
                            >
                              <option value="">
                                {branchSel === 'all' ? '-- Semua Router Aktif --' : '-- Semua Router di Cabang Ini --'}
                              </option>
                              {filteredRouters.map(router => (
                                <option key={router.id} value={router.id}>
                                  {router.name || router.host} ({router.host}:{router.port})
                                  {router.branch_name ? ` · ${router.branch_name}` : ''}
                                </option>
                              ))}
                            </select>
                            {branchSel !== 'all' && filteredRouters.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1 dark:text-amber-400">
                                ⚠️ Belum ada router aktif yang terhubung ke cabang ini.
                              </p>
                            )}
                          </>
                        );
                      })()}
                      {mikrotikRouters.length === 0 && (
                        <p className="text-xs text-orange-600 dark:text-orange-400">
                          ⚠️ Belum ada Router MikroTik. <a href="/app/mikrotik" className="underline font-medium">Tambah Router dulu</a>
                        </p>
                      )}
                      {formData.selected_router_id && (
                        <div className="text-xs text-emerald-700 bg-emerald-100 p-2 rounded dark:text-emerald-300 dark:bg-emerald-900/40">
                          {(() => {
                            const selectedRouterItem = mikrotikRouters.find(r => r.id == formData.selected_router_id);
                            if (selectedRouterItem) {
                              return (
                                <>
                                  <strong>{selectedRouterItem.name || selectedRouterItem.host}</strong><br/>
                                  Host: {selectedRouterItem.host}:{selectedRouterItem.port}<br/>
                                  {selectedRouterItem.branch_name && <>Cabang: {selectedRouterItem.branch_name}</>}
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        💡 Kosongkan untuk membuat profile di semua router aktif
                      </p>
                    </div>
                  )}
                  {/* MikroTik Profile Name for RADIUS */}
                  <div className="p-2 border border-slate-200 rounded-lg dark:border-slate-700">
                    <label className="block text-xs font-medium text-slate-700 mb-1 dark:text-slate-200">
                      Nama Profile MikroTik (untuk RADIUS)
                    </label>
                    <input
                      type="text"
                      value={formData.mikrotik_profile_name || ''}
                      onChange={(e) => setFormData({...formData, mikrotik_profile_name: e.target.value})}
                      placeholder="Contoh: SILVER_180, GOLD_300"
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600"
                    />
                  </div>
                  
                  <label className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors dark:border-slate-700 dark:hover:bg-slate-700/50">
                    <input 
                      type="checkbox" 
                      id="is-active" 
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})} 
                      className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 dark:text-indigo-400" 
                    />
                    <div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Aktifkan paket</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 p-2 border border-indigo-200 bg-indigo-50/30 rounded-lg hover:bg-indigo-50 cursor-pointer transition-colors dark:border-indigo-800 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/40">
                    <input
                      type="checkbox"
                      id="is-public"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({...formData, is_public: e.target.checked})}
                      className="mt-0.5 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 dark:text-indigo-400"
                      data-testid="form-is-public"
                    />
                    <div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Tampil di Form Daftar Publik</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">Bila non-aktif, paket ini tidak muncul di form <span className="font-mono">daftar.kumarahotspot.com</span>.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-2 p-2 border border-amber-200 bg-amber-50/30 rounded-lg hover:bg-amber-50 cursor-pointer transition-colors dark:border-amber-800 dark:bg-amber-950/20 dark:hover:bg-amber-950/40">
                    <input
                      type="checkbox"
                      id="is-featured"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      className="mt-0.5 w-4 h-4 text-amber-600 rounded focus:ring-amber-500 dark:text-amber-400"
                      data-testid="form-is-featured"
                    />
                    <div>
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
                        Tandai sebagai Paket Rekomendasi
                      </p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">Ditampilkan pertama + badge "REKOMENDASI" di form daftar.</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  className="px-4 h-9 text-sm"
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="px-4 h-9 text-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(135deg, ${branding?.primary_color || '#4F46E5'} 0%, ${branding?.secondary_color || '#7c3aed'} 100%)`,
                    color: getContrastText(branding?.primary_color || '#4F46E5'),
                  }}
                >
                  {isSubmitting && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                  )}
                  {isSubmitting 
                    ? (isEditMode ? 'Memperbarui...' : 'Menyimpan...') 
                    : (isEditMode ? 'Update Paket' : 'Simpan Paket')
                  }
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
        )}
      </div>

      {/* Sync to MikroTik Dialog */}
      <Dialog open={isSyncDialogOpen} onOpenChange={(open) => { setIsSyncDialogOpen(open); if (!open) setSelectedRouter('all'); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              Push ke MikroTik
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 overflow-y-auto flex-1 pr-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Sinkronkan semua Paket Layanan ke MikroTik sebagai PPPoE/Hotspot Profile.
            </p>
            
            {/* Router Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Pilih Router MikroTik</Label>
              <select
                className="w-full h-10 px-3 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-700 dark:bg-slate-800"
                value={selectedRouter}
                onChange={(e) => setSelectedRouter(e.target.value)}
              >
                <option value="all">🌐 Semua Router Aktif</option>
                {mikrotikRouters.filter(r => r.is_active).map(router => (
                  <option key={router.id} value={router.id}>
                    📡 {router.name || router.router_name || router.host} ({router.host}:{router.port})
                  </option>
                ))}
              </select>
            </div>
            
            {/* IP Pool Configuration */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3 dark:bg-blue-900/30">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-800">🌐 Konfigurasi IP Pool</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs text-blue-700 dark:text-blue-300">Nama Pool</Label>
                  <Input 
                    value={poolConfig.name}
                    onChange={(e) => setPoolConfig({...poolConfig, name: e.target.value})}
                    className="h-8 text-sm mt-1"
                    placeholder="RadiusLink"
                  />
                </div>
                <div>
                  <Label className="text-xs text-blue-700 dark:text-blue-300">Range Start</Label>
                  <Input 
                    value={poolConfig.rangeStart}
                    onChange={(e) => setPoolConfig({...poolConfig, rangeStart: e.target.value})}
                    className="h-8 text-sm mt-1"
                    placeholder="172.16.0.2"
                  />
                </div>
                <div>
                  <Label className="text-xs text-blue-700 dark:text-blue-300">Range End</Label>
                  <Input 
                    value={poolConfig.rangeEnd}
                    onChange={(e) => setPoolConfig({...poolConfig, rangeEnd: e.target.value})}
                    className="h-8 text-sm mt-1"
                    placeholder="172.16.31.254"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-blue-700 dark:text-blue-300">Local Address (Gateway)</Label>
                  <Input 
                    value={poolConfig.localAddress}
                    onChange={(e) => setPoolConfig({...poolConfig, localAddress: e.target.value})}
                    className="h-8 text-sm mt-1"
                    placeholder="172.16.0.1"
                  />
                </div>
              </div>
            </div>
            
            {/* Expired Pool Configuration */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-3 dark:bg-orange-900/30">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-orange-800">🚫 IP Pool Expired (Isolir)</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={poolConfig.expiredEnabled}
                    onChange={(e) => setPoolConfig({...poolConfig, expiredEnabled: e.target.checked})}
                    className="w-4 h-4 rounded border-orange-300"
                  />
                  <span className="text-xs text-orange-700 dark:text-orange-300">Aktifkan</span>
                </label>
              </div>
              
              {poolConfig.expiredEnabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs text-orange-700 dark:text-orange-300">Nama Pool Expired</Label>
                    <Input 
                      value={poolConfig.expiredName}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredName: e.target.value})}
                      className="h-8 text-sm mt-1 border-orange-200"
                      placeholder="RadiusLink-Expired"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-700 dark:text-orange-300">Range Start</Label>
                    <Input 
                      value={poolConfig.expiredRangeStart}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredRangeStart: e.target.value})}
                      className="h-8 text-sm mt-1 border-orange-200"
                      placeholder="10.10.99.2"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-700 dark:text-orange-300">Range End</Label>
                    <Input 
                      value={poolConfig.expiredRangeEnd}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredRangeEnd: e.target.value})}
                      className="h-8 text-sm mt-1 border-orange-200"
                      placeholder="10.10.99.254"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-700 dark:text-orange-300">Local Address</Label>
                    <Input 
                      value={poolConfig.expiredLocalAddress}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredLocalAddress: e.target.value})}
                      className="h-8 text-sm mt-1 border-orange-200"
                      placeholder="10.10.99.1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-orange-700 dark:text-orange-300">Bandwidth</Label>
                    <select 
                      value={poolConfig.expiredBandwidth}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredBandwidth: e.target.value})}
                      className="w-full h-8 px-2 text-sm mt-1 border border-orange-200 rounded-md"
                    >
                      <option value="64k">64 Kbps</option>
                      <option value="128k">128 Kbps</option>
                      <option value="256k">256 Kbps</option>
                      <option value="512k">512 Kbps</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-orange-700 dark:text-orange-300">URL Redirect Halaman Isolir (opsional)</Label>
                    <Input 
                      value={poolConfig.expiredRedirectUrl}
                      onChange={(e) => setPoolConfig({...poolConfig, expiredRedirectUrl: e.target.value})}
                      className="h-8 text-sm mt-1 border-orange-200"
                      placeholder="http://billing.radiuslink.id/isolir"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 dark:bg-emerald-900/30">
              <p className="text-sm text-emerald-800">
                <strong>{plans.filter(p => p.is_active).length}</strong> paket aktif akan di-sync ke {selectedRouter === 'all' ? 'semua router' : 'router terpilih'}
              </p>
              {poolConfig.expiredEnabled && (
                <p className="text-xs text-emerald-700 mt-1 dark:text-emerald-300">
                  + Profile EXPIRED dengan redirect ke halaman isolir
                </p>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ⚠️ IP Pool akan dibuat jika belum ada. Profile yang sudah ada akan di-update.
            </p>
          </div>
          <div className="flex justify-end gap-2 flex-shrink-0 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsSyncDialogOpen(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleSyncToMikrotik}
              disabled={isSyncing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Sync Sekarang
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import from MikroTik Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={(open) => { setIsImportDialogOpen(open); if (!open) { setSelectedRouter('all'); setSelectedRouterInfo(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Import Profile dari MikroTik
            </DialogTitle>
          </DialogHeader>
          
          {/* Router Selection for Import */}
          <div className="py-2 border-b">
            <div className="flex items-center gap-3">
              <Label className="text-sm font-medium whitespace-nowrap">Router:</Label>
              <select
                className="flex-1 h-9 px-3 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm dark:border-slate-700 dark:bg-slate-800"
                value={selectedRouter}
                onChange={(e) => { setSelectedRouter(e.target.value); loadMikrotikProfiles(e.target.value); }}
              >
                <option value="all">-- Pilih Router --</option>
                {mikrotikRouters.filter(r => r.is_active).map(router => (
                  <option key={router.id} value={router.id}>
                    {router.name || router.router_name || router.host} ({router.host}:{router.port})
                  </option>
                ))}
              </select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => loadMikrotikProfiles(selectedRouter)}
                disabled={isLoadingProfiles || selectedRouter === 'all'}
              >
                <RefreshCw className={`w-4 h-4 ${isLoadingProfiles ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {selectedRouterInfo && (
              <p className="text-xs text-slate-500 mt-2 dark:text-slate-400">
                📡 Terhubung ke: <strong>{selectedRouterInfo.name}</strong> ({selectedRouterInfo.host})
              </p>
            )}
          </div>
          
          {selectedRouter === 'all' ? (
            <div className="py-8 text-center">
              <Download className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400">Pilih router MikroTik untuk melihat profiles</p>
            </div>
          ) : isLoadingProfiles ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3 dark:text-blue-400" />
              <p className="text-slate-600 dark:text-slate-300">Memuat profiles dari MikroTik...</p>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              {/* PPPoE Profiles */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 dark:text-slate-200">
                  <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  PPPoE Profiles ({mikrotikProfiles.ppp.length})
                </h3>
                {mikrotikProfiles.ppp.length === 0 ? (
                  <p className="text-sm text-slate-500 italic dark:text-slate-400">Tidak ada PPP profile ditemukan</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {mikrotikProfiles.ppp.map(profile => (
                      <div 
                        key={`ppp-${profile.name}`}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedProfiles.some(p => p.name === profile.name && p.type === 'pppoe')
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300'
                        }`}
                        onClick={() => toggleProfileSelection(profile, 'pppoe')}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{profile.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {profile.download_speed || '?'}M/{profile.upload_speed || '?'}M
                          </Badge>
                        </div>
                        {profile.rate_limit && (
                          <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Rate: {profile.rate_limit}</p>
                        )}
                        {selectedProfiles.some(p => p.name === profile.name && p.type === 'pppoe') && (
                          <div className="mt-2">
                            <Label className="text-xs">Harga (Rp)</Label>
                            <Input
                              type="number"
                              placeholder="0"
                              className="h-8 text-sm mt-1"
                              value={importPrices[profile.name] || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                setImportPrices(prev => ({
                                  ...prev,
                                  [profile.name]: parseInt(e.target.value) || 0
                                }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hotspot Profiles */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 dark:text-slate-200">
                  <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  Hotspot Profiles ({mikrotikProfiles.hotspot.length})
                </h3>
                {mikrotikProfiles.hotspot.length === 0 ? (
                  <p className="text-sm text-slate-500 italic dark:text-slate-400">Tidak ada Hotspot profile ditemukan</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {mikrotikProfiles.hotspot.map(profile => (
                      <div 
                        key={`hotspot-${profile.name}`}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedProfiles.some(p => p.name === profile.name && p.type === 'hotspot')
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-slate-200 hover:border-amber-300'
                        }`}
                        onClick={() => toggleProfileSelection(profile, 'hotspot')}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{profile.name}</span>
                          <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30">
                            {profile.download_speed || '?'}M/{profile.upload_speed || '?'}M
                          </Badge>
                        </div>
                        {profile.rate_limit && (
                          <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">Rate: {profile.rate_limit}</p>
                        )}
                        {selectedProfiles.some(p => p.name === profile.name && p.type === 'hotspot') && (
                          <div className="mt-2">
                            <Label className="text-xs">Harga (Rp)</Label>
                            <Input
                              type="number"
                              placeholder="0"
                              className="h-8 text-sm mt-1"
                              value={importPrices[profile.name] || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                setImportPrices(prev => ({
                                  ...prev,
                                  [profile.name]: parseInt(e.target.value) || 0
                                }));
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected count */}
              {selectedProfiles.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/30">
                  <p className="text-sm text-blue-800">
                    <strong>{selectedProfiles.length}</strong> profile dipilih untuk diimport
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end items-center border-t pt-4 gap-2">
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleImportProfiles}
              disabled={isSyncing || selectedProfiles.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Import ({selectedProfiles.length})
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border border-slate-200/70 dark:border-slate-700/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                <Package className="w-6 h-6 text-white drop-shadow" strokeWidth={2.3} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider dark:text-slate-400">Total Paket</p>
                <p className="text-2xl font-extrabold text-slate-900 dark:text-slate-100 leading-none mt-1">{plans.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200/70 dark:border-slate-700/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
                <ToggleRight className="w-6 h-6 text-white drop-shadow" strokeWidth={2.3} />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider dark:text-slate-400">Aktif</p>
                <p className="text-2xl font-extrabold text-emerald-600 leading-none mt-1 dark:text-emerald-400">{plans.filter(p => p.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200/70 dark:border-slate-700/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' }}>
                <Zap className="w-6 h-6 text-white drop-shadow" strokeWidth={2.3} fill="white" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider dark:text-slate-400">PPPoE</p>
                <p className="text-2xl font-extrabold text-blue-600 leading-none mt-1 dark:text-blue-400">{plans.filter(p => p.package_type === 'pppoe').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200/70 dark:border-slate-700/70 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #ea580c 0%, #f97316 100%)' }}>
                <Zap className="w-6 h-6 text-white drop-shadow" strokeWidth={2.3} fill="white" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider dark:text-slate-400">Hotspot</p>
                <p className="text-2xl font-extrabold text-orange-600 leading-none mt-1 dark:text-orange-400">{plans.filter(p => p.package_type === 'hotspot').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Bar */}
      <Card className="border-0 shadow-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            {/* Connection Type Tabs */}
            <div className="flex bg-slate-100 rounded-lg p-1 dark:bg-slate-800">
              <button onClick={() => setConnectionTypeFilter('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  connectionTypeFilter === 'all'
                    ? 'bg-white shadow text-indigo-600'
                    : 'text-slate-600 hover:text-slate-800'
                }`}>
                Semua
              </button>
              <button onClick={() => setConnectionTypeFilter('pppoe')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  connectionTypeFilter === 'pppoe'
                    ? 'bg-blue-600 shadow text-white'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}>
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                PPPoE
              </button>
              <button onClick={() => setConnectionTypeFilter('hotspot')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  connectionTypeFilter === 'hotspot'
                    ? 'bg-orange-500 shadow text-white'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}>
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                Hotspot
              </button>
            </div>
            
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <Input placeholder="Cari paket..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-10 bg-white border-slate-200 dark:bg-slate-800 dark:border-slate-700" />
            </div>
            
            <div className="text-sm text-slate-500 flex items-center dark:text-slate-400">
              <span className="bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-semibold dark:bg-indigo-900/40 dark:text-indigo-300">
                {filteredPlans.length} paket
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Packages Grid - Card Based Design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredPlans.length === 0 ? (
          <div className="col-span-full">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 dark:bg-slate-800">
                  <Package className="w-6 h-6 text-slate-400 dark:text-slate-500" />
                </div>
                <p className="text-slate-600 font-medium text-sm dark:text-slate-300">
                  {searchTerm ? 'Tidak ada paket yang cocok' : 'Belum ada paket'}
                </p>
                <p className="text-xs text-slate-400 mt-1 dark:text-slate-500">Klik &quot;Tambah Paket&quot; untuk membuat paket baru</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredPlans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative border border-slate-200/70 dark:border-slate-700/70 shadow-sm hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600 hover:-translate-y-0.5 transition-all duration-200 overflow-hidden ${!plan.is_active ? 'opacity-60' : ''}`}
            >
              {/* Left accent bar (vertical, subtle) */}
              <div 
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ 
                  background: plan.is_active 
                    ? plan.package_type === 'pppoe'
                      ? 'linear-gradient(180deg, #2563eb, #3b82f6)'
                      : 'linear-gradient(180deg, #ea580c, #f97316)'
                    : 'linear-gradient(180deg, #94a3b8, #cbd5e1)'
                }}
              />
              <CardContent className="p-4 pl-5">
                {/* Top row: type badge + status pill (right side) */}
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide ${
                    plan.package_type === 'pppoe' 
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' 
                      : 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  }`}>
                    {plan.package_type === 'pppoe' ? 'PPPoE' : 'Hotspot'}
                  </span>
                  <button 
                    onClick={() => toggleStatus(plan)}
                    disabled={isOwnerRole}
                    className={`flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full transition-colors ${
                      plan.is_active 
                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' 
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    } ${isOwnerRole ? 'cursor-default' : ''}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${plan.is_active ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                    {plan.is_active ? 'Aktif' : 'Nonaktif'}
                  </button>
                </div>

                {/* Plan name */}
                <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 truncate leading-tight" title={plan.name}>
                  {plan.is_featured ? <Star className="inline w-4 h-4 mr-1 -mt-1 text-amber-500 fill-amber-400" /> : null}
                  {plan.name}
                </h3>
                {plan.branch_id && plan.branch_id !== 'all' && (
                  <p className="text-[10px] text-indigo-600 dark:text-indigo-300 font-medium mt-0.5 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 rounded-full bg-indigo-500" />
                    {getBranchName(plan)}
                  </p>
                )}
                {plan.description && (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5" title={plan.description}>{plan.description}</p>
                )}

                {/* Public + Featured quick toggles */}
                <div className="flex items-center gap-1.5 mt-2">
                  <button
                    type="button"
                    onClick={() => togglePublic(plan)}
                    disabled={isOwnerRole}
                    className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                      (plan.is_public === undefined ? true : !!plan.is_public)
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800'
                        : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                    } ${isOwnerRole ? 'cursor-default opacity-70' : ''}`}
                    title={(plan.is_public === undefined ? true : !!plan.is_public) ? 'Tampil di form daftar publik — klik untuk sembunyikan' : 'Tersembunyi dari form daftar — klik untuk tampilkan'}
                    data-testid={`toggle-public-${plan.id}`}
                  >
                    {(plan.is_public === undefined ? true : !!plan.is_public)
                      ? <><Eye className="w-3 h-3" /> Publik</>
                      : <><EyeOff className="w-3 h-3" /> Sembunyi</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFeatured(plan)}
                    disabled={isOwnerRole}
                    className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
                      plan.is_featured
                        ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                        : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-700'
                    } ${isOwnerRole ? 'cursor-default opacity-70' : ''}`}
                    title={plan.is_featured ? 'Rekomendasi — klik untuk cabut' : 'Tandai sebagai Paket Rekomendasi'}
                    data-testid={`toggle-featured-${plan.id}`}
                  >
                    <Star className={`w-3 h-3 ${plan.is_featured ? 'fill-amber-400' : ''}`} />
                    {plan.is_featured ? 'Rekomendasi' : 'Rekomendasikan'}
                  </button>
                </div>

                {/* Speed pill row */}
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800/50">
                    <ArrowDown className="w-3 h-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
                    <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-300">{plan.download_speed} Mbps</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-sky-50 dark:bg-sky-900/30 border border-sky-100 dark:border-sky-800/50">
                    <ArrowUp className="w-3 h-3 text-sky-600 dark:text-sky-400" strokeWidth={2.5} />
                    <span className="text-[11px] font-bold text-sky-700 dark:text-sky-300">{plan.upload_speed} Mbps</span>
                  </div>
                  {plan.speed_type === 'Burst' && plan.burst_download_speed > 0 && (
                    <div className="flex items-center gap-0.5 px-1.5 py-1 rounded-md bg-purple-50 dark:bg-purple-900/30 border border-purple-100 dark:border-purple-800/50">
                      <Zap className="w-3 h-3 text-purple-600 dark:text-purple-400 fill-purple-500" />
                      <span className="text-[10px] font-bold text-purple-700 dark:text-purple-300">Burst</span>
                    </div>
                  )}
                </div>

                {/* Price block */}
                <div className="flex items-baseline gap-1 mt-3 pb-3 border-b border-slate-100 dark:border-slate-700/50">
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">Rp</span>
                  <span className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                    {Number(plan.price || 0).toLocaleString("id-ID")}
                  </span>
                  <span className="text-[11px] text-slate-400 ml-0.5 dark:text-slate-500">/ {formatDurationLabel(plan)}</span>
                </div>

                {/* Action row */}
                <div className="flex items-center gap-1.5 mt-3">
                  <Button
                    onClick={() => toggleStatus(plan)}
                    size="sm"
                    variant={plan.is_active ? 'outline' : 'default'}
                    className={`flex-1 h-8 text-[11px] font-medium ${plan.is_active ? 'hover:bg-slate-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white border-0'}`}
                    disabled={isOwnerRole}
                  >
                    {plan.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                  </Button>
                  {!isOwnerRole && (
                  <>
                  <Button
                    onClick={() => handleEdit(plan)}
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300 dark:hover:bg-blue-900/30"
                    title="Edit paket"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(plan.id)}
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 hover:border-red-300 dark:hover:bg-red-900/30"
                    title="Hapus paket"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                  </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default ServicePlans;
